import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Loader2, CheckCircle2, XCircle, Clock, Calendar, RefreshCw, Save, Video, Phone } from "lucide-react";

function fmt(iso) {
  try { return new Date(iso).toLocaleString("en-US", { timeZone: "America/Los_Angeles" }) + " PT"; } catch { return iso; }
}

export default function AdminGriefAppointments() {
  const [tab, setTab] = useState("requests");
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [smsStatus, setSmsStatus] = useState(null);
  const [avail, setAvail] = useState(null);
  const [saveMsg, setSaveMsg] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [rowErr, setRowErr] = useState({});
  const [zoomInputs, setZoomInputs] = useState({});
  const [proposeInputs, setProposeInputs] = useState({});
  const [noteInputs, setNoteInputs] = useState({});

  const load = async () => {
    try {
      const [a, s, av] = await Promise.all([
        api.get(`/admin/grief/appointments`),
        api.get(`/admin/grief/status/sms`),
        api.get(`/admin/grief/availability`),
      ]);
      setAppts(a.data || []);
      setSmsStatus(s.data);
      setAvail(av.data);
    } finally { setLoading(false); setRefreshing(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const doApprove = async (id) => {
    setBusyId(id); setRowErr((e) => ({ ...e, [id]: "" }));
    try {
      await api.post(`/admin/grief/appointments/${id}/approve`, {
        zoom_link: zoomInputs[id] || null,
        admin_note: noteInputs[id] || null,
      });
      await load();
    } catch (e) {
      setRowErr((r) => ({ ...r, [id]: e?.response?.data?.detail || "approve_failed" }));
    } finally { setBusyId(null); }
  };
  const doDecline = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/grief/appointments/${id}/decline`, { admin_note: noteInputs[id] || null });
      await load();
    } finally { setBusyId(null); }
  };
  const doPropose = async (id) => {
    if (!proposeInputs[id]) { setRowErr((r) => ({ ...r, [id]: "Enter a proposed ISO time first." })); return; }
    setBusyId(id); setRowErr((e) => ({ ...e, [id]: "" }));
    try {
      await api.post(`/admin/grief/appointments/${id}/propose`, {
        proposed_slot_start_iso: proposeInputs[id],
        admin_note: noteInputs[id] || null,
      });
      await load();
    } catch (e) {
      setRowErr((r) => ({ ...r, [id]: e?.response?.data?.detail || "propose_failed" }));
    } finally { setBusyId(null); }
  };

  const saveAvail = async () => {
    setSaveMsg("");
    const body = {
      ...avail,
      days: (avail.days || []).map(Number),
      blocked_dates: (typeof avail._blocked_str === "string" ? avail._blocked_str : (avail.blocked_dates || []).join(","))
        .split(",").map((s) => s.trim()).filter(Boolean),
      session_minutes: Number(avail.session_minutes || 60),
      gap_minutes: Number(avail.gap_minutes || 15),
      booking_window_days: Number(avail.booking_window_days || 30),
    };
    delete body._blocked_str;
    try {
      const r = await api.put(`/admin/grief/availability`, body);
      setAvail({ ...r.data });
      setSaveMsg("Saved.");
    } catch { setSaveMsg("Save failed."); }
  };

  if (loading) return <div className="p-6 text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div className="p-6 max-w-6xl" data-testid="admin-grief-page">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-2xl tracking-tight text-slate-900">Grief Counseling Appointments</h2>
        <button onClick={() => { setRefreshing(true); load(); }} className="inline-flex items-center gap-2 text-sm text-[#4A6FA5]"><RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh</button>
      </div>

      {smsStatus && (
        <div className={`text-sm rounded-lg p-3 mb-4 ${smsStatus.configured ? "bg-emerald-50 border border-emerald-200 text-emerald-900" : "bg-amber-50 border border-amber-200 text-amber-900"}`} data-testid="admin-grief-sms-status">
          SMS alerts: <strong>{smsStatus.configured ? "Active" : "Not configured"}</strong>
          {!smsStatus.configured && <span className="ml-2">(set TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_FROM_NUMBER, GRIEF_ADMIN_SMS_TO to enable)</span>}
        </div>
      )}

      <div className="flex gap-2 mb-4 border-b border-slate-200">
        {["requests", "availability"].map((k) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-grief-tab-${k}`} className={`px-4 py-2 text-sm font-medium ${tab === k ? "border-b-2 border-[#4A6FA5] text-[#4A6FA5]" : "text-slate-500"}`}>{k === "requests" ? "Requests" : "Availability"}</button>
        ))}
      </div>

      {tab === "requests" && (
        <div className="space-y-3" data-testid="admin-grief-requests">
          {appts.length === 0 && <p className="text-sm text-slate-500">No requests yet.</p>}
          {appts.map((a) => (
            <article key={a.id} data-testid={`admin-grief-row-${a.id}`} className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-slate-900">{a.first_name} {a.last_name} <span className="ml-2 text-xs uppercase tracking-[0.16em] text-slate-500">{a.status}</span></p>
                  <p className="text-sm text-slate-600">{a.email} · {a.phone}</p>
                  <p className="text-sm text-slate-700 mt-1 inline-flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5 text-[#4A6FA5]" /> {fmt(a.slot_start_iso)}
                    <span className="ml-2 inline-flex items-center gap-1 text-slate-500">
                      {a.format === "phone" ? <Phone className="w-3.5 h-3.5" /> : <Video className="w-3.5 h-3.5" />} {a.format}
                    </span>
                  </p>
                  {a.proposed_slot_start_iso && (
                    <p className="text-xs text-amber-800 mt-1">Proposed: {fmt(a.proposed_slot_start_iso)} (awaiting client)</p>
                  )}
                  <p className="text-[11px] text-slate-500 mt-2">
                    Client ack email: {a.client_acknowledgment_sent ? "sent" : "not sent"} · Confirmation email: {a.client_confirmation_sent ? "sent" : "not sent"} · SMS: {a.sms_alert?.sent ? "sent" : (a.sms_alert?.error || "not attempted")}
                  </p>
                </div>
              </div>

              {a.status === "pending" && (
                <div className="mt-4 grid gap-3">
                  <div className="grid sm:grid-cols-3 gap-2">
                    <input placeholder="Zoom link (optional)" value={zoomInputs[a.id] || ""} onChange={(e) => setZoomInputs((x) => ({ ...x, [a.id]: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid={`admin-grief-zoom-${a.id}`} />
                    <input placeholder="Propose ISO time (e.g. 2026-02-18T14:00:00-08:00)" value={proposeInputs[a.id] || ""} onChange={(e) => setProposeInputs((x) => ({ ...x, [a.id]: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid={`admin-grief-propose-${a.id}`} />
                    <input placeholder="Note to client (optional)" value={noteInputs[a.id] || ""} onChange={(e) => setNoteInputs((x) => ({ ...x, [a.id]: e.target.value }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid={`admin-grief-note-${a.id}`} />
                  </div>
                  {rowErr[a.id] && <p className="text-xs text-red-700">{rowErr[a.id]}</p>}
                  <div className="flex flex-wrap gap-2">
                    <button disabled={busyId === a.id} onClick={() => doApprove(a.id)} className="inline-flex items-center gap-2 rounded-lg bg-[#4A6FA5] text-white px-4 py-2 text-sm font-medium hover:bg-[#3B597B] disabled:opacity-60" data-testid={`admin-grief-approve-${a.id}`}>
                      <CheckCircle2 className="w-4 h-4" /> Approve
                    </button>
                    <button disabled={busyId === a.id} onClick={() => doPropose(a.id)} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 text-slate-800 px-4 py-2 text-sm font-medium hover:border-[#4A6FA5]" data-testid={`admin-grief-propose-btn-${a.id}`}>
                      <Clock className="w-4 h-4" /> Propose another time
                    </button>
                    <button disabled={busyId === a.id} onClick={() => doDecline(a.id)} className="inline-flex items-center gap-2 rounded-lg border border-red-200 text-red-700 px-4 py-2 text-sm font-medium hover:bg-red-50" data-testid={`admin-grief-decline-${a.id}`}>
                      <XCircle className="w-4 h-4" /> Decline
                    </button>
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {tab === "availability" && avail && (
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="admin-grief-availability">
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="text-sm"><span className="block text-xs uppercase tracking-[0.16em] font-semibold text-slate-600 mb-1">Days (0=Mon…6=Sun, comma-separated)</span>
              <input value={(avail.days || []).join(",")} onChange={(e) => setAvail((a) => ({ ...a, days: e.target.value.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n)) }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" /></label>
            <label className="text-sm"><span className="block text-xs uppercase tracking-[0.16em] font-semibold text-slate-600 mb-1">Session minutes</span>
              <input type="number" value={avail.session_minutes} onChange={(e) => setAvail((a) => ({ ...a, session_minutes: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" /></label>
            <label className="text-sm"><span className="block text-xs uppercase tracking-[0.16em] font-semibold text-slate-600 mb-1">Start time (HH:MM)</span>
              <input value={avail.start_time} onChange={(e) => setAvail((a) => ({ ...a, start_time: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" /></label>
            <label className="text-sm"><span className="block text-xs uppercase tracking-[0.16em] font-semibold text-slate-600 mb-1">End time (HH:MM)</span>
              <input value={avail.end_time} onChange={(e) => setAvail((a) => ({ ...a, end_time: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" /></label>
            <label className="text-sm"><span className="block text-xs uppercase tracking-[0.16em] font-semibold text-slate-600 mb-1">Gap between appointments (minutes)</span>
              <input type="number" value={avail.gap_minutes} onChange={(e) => setAvail((a) => ({ ...a, gap_minutes: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" /></label>
            <label className="text-sm"><span className="block text-xs uppercase tracking-[0.16em] font-semibold text-slate-600 mb-1">Booking window (days ahead)</span>
              <input type="number" value={avail.booking_window_days} onChange={(e) => setAvail((a) => ({ ...a, booking_window_days: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" /></label>
            <label className="text-sm sm:col-span-2"><span className="block text-xs uppercase tracking-[0.16em] font-semibold text-slate-600 mb-1">Blocked dates (YYYY-MM-DD, comma-separated)</span>
              <input value={avail._blocked_str ?? (avail.blocked_dates || []).join(",")} onChange={(e) => setAvail((a) => ({ ...a, _blocked_str: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2" /></label>
            <label className="text-sm sm:col-span-2 inline-flex items-center gap-2"><input type="checkbox" checked={!!avail.enabled} onChange={(e) => setAvail((a) => ({ ...a, enabled: e.target.checked }))} data-testid="admin-grief-availability-enabled" />Enabled (public calendar shows slots)</label>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button onClick={saveAvail} className="inline-flex items-center gap-2 rounded-lg bg-[#4A6FA5] text-white px-4 py-2 text-sm font-medium hover:bg-[#3B597B]" data-testid="admin-grief-availability-save"><Save className="w-4 h-4" /> Save availability</button>
            {saveMsg && <span className="text-sm text-slate-600">{saveMsg}</span>}
          </div>
          <p className="mt-4 text-xs text-slate-500">Timezone fixed to America/Los_Angeles. Slot generation excludes past times, blocked dates, and any pending/approved/proposed appointments.</p>
        </div>
      )}
    </div>
  );
}
